import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getImagePath } from '../utils/imagePath';
import './LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1); // 1 = Welcome Page (full green), 2 = Login Form
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 300, damping: 24 }
    }
  };

  const pageVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    },
    exit: { 
      opacity: 0, 
      x: -50,
      transition: { duration: 0.3 }
    }
  };

  useEffect(() => {
    setIsPageLoaded(true);
    
    // Check if user is already logged in
    const token = localStorage.getItem('nlngra_token');
    if (token) {
      console.log('User already logged in, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      return;
    }
    
    const rememberedEmail = localStorage.getItem('remembered_email');
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, [navigate]);

  const goToLogin = () => {
    setCurrentPage(2);
  };

  const goBackToWelcome = () => {
    setCurrentPage(1);
    setMessage('');
    setIsError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setIsError(false);

    if (!formData.email.trim() || !formData.password) {
      setMessage('Please enter both email and password');
      setIsError(true);
      setLoading(false);
      return;
    }

    try {
      const url = 'https://nlngraclub.com/nlnapi/index.php?url=login';
      
      console.log('🔐 Attempting login to:', url);
      console.log('📧 Email:', formData.email);
      
      const requestBody = JSON.stringify({
        email: formData.email.trim(),
        password: formData.password
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: requestBody
      });

      const responseText = await response.text();
      console.log('📥 Raw login response:', responseText);
      
      let cleanResponse = responseText;
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>Warning<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/<br \/>\s*<b>.*?<\/b>.*?\n/g, '');
      cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
      
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      
      let data;
      if (jsonMatch) {
        try {
          data = JSON.parse(jsonMatch[0]);
          console.log('✅ Successfully extracted and parsed JSON:', data);
        } catch (parseError) {
          console.error('Failed to parse extracted JSON:', parseError);
          setMessage('Server returned an invalid response. Please try again.');
          setIsError(true);
          setLoading(false);
          return;
        }
      } else {
        try {
          data = JSON.parse(cleanResponse.trim());
        } catch (e) {
          console.error('No JSON found in response');
          setMessage('Server returned invalid response. Please try again.');
          setIsError(true);
          setLoading(false);
          return;
        }
      }
      
      console.log('📥 Parsed login response:', data);
      
      if (data.success === true) {
        setMessage(data.message || 'Login successful! Redirecting...');
        setIsError(false);
        
        if (data.token) {
          localStorage.setItem('nlngra_token', data.token);
          localStorage.setItem('nlngra_jwt', data.token);
          console.log('✅ Token stored:', data.token.substring(0, 20) + '...');
        }
        
        if (data.session_token) {
          localStorage.setItem('nlngra_session', data.session_token);
        }
        
        if (data.user) {
          localStorage.setItem('nlngra_user', JSON.stringify(data.user));
          console.log('✅ User stored:', data.user.email);
        }
        
        if (data.dependents && data.dependents.length > 0) {
          localStorage.setItem('nlngra_dependents', JSON.stringify(data.dependents));
        }
        
        if (rememberMe) {
          localStorage.setItem('remembered_email', formData.email);
        } else {
          localStorage.removeItem('remembered_email');
        }
        
        setFormData({...formData, password: ''});
        window.dispatchEvent(new Event('storage'));
        
        console.log('✅ Login successful! Redirecting to dashboard...');
        
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
        
      } else {
        let errorMsg = data.message || data.error || 'Login failed';
        
        if (errorMsg.toLowerCase().includes('pending approval')) {
          errorMsg = 'Your account is pending approval. Please wait for admin approval or contact support.';
        } else if (errorMsg.toLowerCase().includes('suspended')) {
          errorMsg = 'Your account has been suspended. Please contact support.';
        } else if (errorMsg.toLowerCase().includes('not found')) {
          errorMsg = 'Account not found. Please check your email/membership number.';
        } else if (errorMsg.toLowerCase().includes('invalid password')) {
          errorMsg = 'Invalid password. Please try again.';
        }
        
        setMessage(errorMsg);
        setIsError(true);
        setFormData({...formData, password: ''});
      }
      
    } catch (error) {
      console.error('❌ Login error:', error);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Connection issue. Please try again or contact support.';
      }
      
      setMessage(errorMessage);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isPageLoaded && (
        <motion.div 
          className="login-page"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="login-container">
            <AnimatePresence mode="wait">
              {/* PAGE 1 - FULL SCREEN WELCOME (GREEN GRADIENT WITH IMAGE) - NO CARD */}
              {currentPage === 1 && (
                <motion.div 
                  key="welcome"
                  className="welcome-fullscreen"
                  variants={pageVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ backgroundImage: `url(${getImagePath('images/company/abuja-boulevard.jpg')})` }}
                >
                  <div className="welcome-overlay-full"></div>
                  <div className="welcome-content-full">
                    <motion.div 
                      className="welcome-logo-full"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2, type: 'spring', duration: 0.6 }}
                    >
                      <img src={getImagePath('images/logo.png')} alt="NLNG RA Club" />
                    </motion.div>
                    
                    <motion.h1
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                    >
                      NLNG RA Club
                    </motion.h1>
                    
                    <motion.p
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4, duration: 0.5 }}
                    >
                      Experience premium living, world-class facilities, and a vibrant community.
                    </motion.p>
                    
                    <motion.div 
                      className="welcome-features-full"
                      initial="hidden"
                      animate="visible"
                      variants={containerVariants}
                    >
                      {[
                        { icon: 'fa-calendar-check', text: 'Book Facilities' },
                        { icon: 'fa-users', text: 'Join Sections' },
                        { icon: 'fa-bell', text: 'Event Notifications' },
                        { icon: 'fa-chart-line', text: 'Member Dashboard' }
                      ].map((feature, index) => (
                        <motion.div 
                          key={index}
                          className="welcome-feature-full"
                          variants={itemVariants}
                          whileHover={{ x: 5, backgroundColor: 'rgba(255,255,255,0.2)' }}
                        >
                          <i className={`fas ${feature.icon}`}></i>
                          <span>{feature.text}</span>
                        </motion.div>
                      ))}
                    </motion.div>
                    
                    <motion.button
                      className="next-btn-full"
                      onClick={goToLogin}
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Get Started <i className="fas fa-arrow-right"></i>
                    </motion.button>
                    
                    <motion.div 
                      className="welcome-footer-full"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                    >
                      <Link to="/register" className="create-account-link-full">
                        <i className="fas fa-user-plus"></i> Create an Account
                      </Link>
                    </motion.div>
                  </div>
                </motion.div>
              )}

              {/* PAGE 2 - LOGIN FORM (WHITE CARD) */}
              {currentPage === 2 && (
                <motion.div 
                  key="login"
                  className="login-form-page"
                  variants={pageVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <div className="login-card">
                    <motion.button 
                      className="back-btn"
                      onClick={goBackToWelcome}
                      whileHover={{ x: -3 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <i className="fas fa-arrow-left"></i> Back
                    </motion.button>
                    
                    <motion.div variants={itemVariants} className="form-header">
                      <motion.div 
                        className="header-badge"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.1, type: 'spring' }}
                      >
                        <i className="fas fa-sign-in-alt"></i> Secure Access
                      </motion.div>
                      <h2>Welcome Back</h2>
                      <p>Sign in to access your member portal and explore premium amenities</p>
                    </motion.div>

                    <AnimatePresence>
                      {message && (
                        <motion.div 
                          className={`alert-message ${isError ? 'alert-error' : 'alert-success'}`}
                          initial={{ opacity: 0, y: -20, height: 0 }}
                          animate={{ opacity: 1, y: 0, height: 'auto' }}
                          exit={{ opacity: 0, y: -20, height: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="alert-icon">
                            <i className={`fas ${isError ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
                          </div>
                          <span>{message}</span>
                          <button className="alert-close" onClick={() => setMessage('')}>
                            <i className="fas fa-times"></i>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <form onSubmit={handleSubmit} className="login-form">
                      <motion.div variants={itemVariants} className="form-group">
                        <label htmlFor="email" className={focusedField === 'email' ? 'focused' : ''}>
                          <i className="fas fa-envelope"></i>
                          Email or Membership Number
                        </label>
                        <div className="input-wrapper">
                          <input
                            type="text"
                            id="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                            onFocus={() => setFocusedField('email')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="Enter your email or membership number"
                            required
                            disabled={loading}
                            className={focusedField === 'email' ? 'focused' : ''}
                          />
                        </div>
                      </motion.div>

                      <motion.div variants={itemVariants} className="form-group">
                        <label htmlFor="password" className={focusedField === 'password' ? 'focused' : ''}>
                          <i className="fas fa-lock"></i>
                          Password
                        </label>
                        <div className="password-input-wrapper">
                          <input
                            type={showPassword ? "text" : "password"}
                            id="password"
                            value={formData.password}
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                            onFocus={() => setFocusedField('password')}
                            onBlur={() => setFocusedField(null)}
                            placeholder="Enter your password"
                            required
                            disabled={loading}
                            className={focusedField === 'password' ? 'focused' : ''}
                          />
                          <motion.button
                            type="button"
                            className="password-toggle-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            whileTap={{ scale: 0.9 }}
                          >
                            <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </motion.button>
                        </div>
                      </motion.div>

                      <motion.div variants={itemVariants} className="form-options">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={() => setRememberMe(!rememberMe)}
                          />
                          <span className="checkmark"></span>
                          <span>Remember me</span>
                        </label>
                        <Link to="/forgot-password" className="forgot-link">
                          <i className="fas fa-question-circle"></i> Forgot Password?
                        </Link>
                      </motion.div>

                      <motion.button
                        type="submit"
                        disabled={loading}
                        className="login-btn"
                        variants={itemVariants}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {loading ? (
                          <>
                            <div className="spinner"></div>
                            Signing in...
                          </>
                        ) : (
                          <>
                            Sign In
                            <i className="fas fa-arrow-right"></i>
                          </>
                        )}
                      </motion.button>

                      <motion.div variants={itemVariants} className="divider">
                        <span>New to NLNG RA Club?</span>
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <Link to="/register" className="register-link">
                          <i className="fas fa-user-plus"></i>
                          Create an Account
                        </Link>
                      </motion.div>
                    </form>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoginPage;